/**
 * Writing a move — the one way the calendar changes an item's time, day,
 * length or person, whether a mouse dragged it on a desktop, a finger on an
 * iPad, or a held finger on a phone (Aaron, Oct 27, note 1.1.4: "click and
 * hold on an event … drag it around into another time slot", saved as the
 * desktop saves it):
 *
 * - a one-off by its own path — a time block's `PATCH /activity/blocks/:id`,
 *   anything else's `PATCH /activities/:id` (`dueAt`, a new length, a new
 *   person);
 * - one occurrence of a repeat, or every one, by
 *   `POST /activity/occurrences/:id/move` (Aaron, Oct 24, small Q3).
 */
import { zonedInstant, type CalendarItem } from '@rowted/shared'
import { activityRequest } from '../api.js'
import { occurrenceOf } from './drag.js'

/** Where a change to an item goes: a place on the grid, a new length when it has one, and a new person when it moved to one. */
export interface MovePlace { date: string; start: number; minutes: number | null; personId: string | null }

export function writeMove(
  item: Pick<CalendarItem, 'id' | 'kind' | 'occurrence' | 'date'>, place: MovePlace, repeat: 'one' | 'all' | null, timeZone: string,
): Promise<unknown> {
  const at = zonedInstant(place.date, place.start, timeZone).toISOString()
  if (repeat) {
    return activityRequest(`/activity/occurrences/${item.id}/move`, { method: 'POST', body: {
      occurrence: occurrenceOf(item), which: repeat, startsAt: at, ...(place.minutes ? { minutes: place.minutes } : {}),
    } })
  }
  if (item.kind === 'block') {
    return activityRequest(`/activity/blocks/${item.id}`, { method: 'PATCH',
      body: { startsAt: at, ...(place.minutes ? { minutes: place.minutes } : {}) } })
  }
  return activityRequest(`/activities/${item.id}`, { method: 'PATCH', body: {
    dueAt: at,
    ...(place.minutes ? { durationMinutes: place.minutes } : {}),
    ...(place.personId ? { participantIds: [place.personId] } : {}),
  } })
}
